import atlas from 'azure-maps-control';
import { PushpinProps } from '../types/MapModels';

/**
 * A wrapper around a map (Azure or legacy) that gives controlled
 * access to certain methods important for getting info out of the map.
 * Does not expose any direct methods of editing the map.
 */
type AzurePushpinUpdater = (pushpinProps: PushpinProps[]) => void;

class MapQuerier {
    private azurePushpinUpdater?: AzurePushpinUpdater;
    private lastPushpins: PushpinProps[] | undefined;

    constructor(public map: any, azurePushpinUpdater?: AzurePushpinUpdater) {
        this.azurePushpinUpdater = azurePushpinUpdater;
    }

    public tryLocationToPixel(
        [lat, lon]: CoordinatePair,
        pixelReference = undefined,
    ): Point | null {
        // Azure Maps stores positions as [lon, lat], while our shared contract uses [lat, lon].
        const azureMap = this.map as atlas.Map | null | undefined;
        if (azureMap?.positionsToPixels) {
            const positions = azureMap.positionsToPixels([[lon, lat]]);
            const first = positions && positions[0];
            if (!first) return null;
            const [x, y] = first;
            return [x as Pixels, y as Pixels];
        }

        if (this.map?.tryLocationToPixel) {
            const point = this.map.tryLocationToPixel(
                { latitude: lat, longitude: lon },
                pixelReference,
            ) || null;
            if (point === null) return null;
            const { x, y } = point;
            return [x as Pixels, y as Pixels];
        }
        return null;
    }

    public tryPixelToLocation(
        [x, y]: Point,
        pixelReference = undefined,
    ): CoordinatePair | null {
        const azureMap = this.map as atlas.Map | null | undefined;
        if (azureMap?.pixelsToPositions) {
            const positions = azureMap.pixelsToPositions([[x, y]]);
            const first = positions && positions[0];
            if (!first) return null;
            const [lon, lat] = first as [number, number];
            return [lat as Latitude, lon as Longitude];
        }

        if (this.map?.tryPixelToLocation) {
            const location = this.map.tryPixelToLocation(
                { x, y },
                pixelReference,
            ) || null;
            if (location === null) return null;
            const { latitude, longitude } = location;
            return [latitude as Latitude, longitude as Longitude];
        }
        return null;
    }


    public existingPushpins: {
        pushpin: any;
        handlerIds: any[];
    }[] = [];

    public updatePushpins(pushpinProps: PushpinProps[]) {
        this.lastPushpins = pushpinProps;
        if (this.azurePushpinUpdater) {
            this.azurePushpinUpdater(pushpinProps);
            return;
        }

        if (this.existingPushpins) {
            this.existingPushpins.forEach(({ pushpin }) => {
                this.map?.entities?.remove?.(pushpin);
            });
            this.existingPushpins = [];
        }
        const pushpins = pushpinProps
            .map(({ location: [lat, lon], options, eventHandlers }) => {
                const pushpin = this.map?.pushpin ? this.map.pushpin : { setOptions: () => null };
                pushpin.location = { latitude: lat, longitude: lon };
                pushpin.options = options;
                const handlerIds = eventHandlers?.map(({ event, callback }) =>
                    this.map?.events?.add?.(pushpin, event, callback),
                ) || [];

                return { pushpin, handlerIds };
            });
        this.map?.entities?.push?.(pushpins.map(({ pushpin }) => pushpin));
        this.existingPushpins.push(...pushpins);
    }

    public removeBusPushpins() {
        if (this.azurePushpinUpdater && this.lastPushpins) {
            const filtered = this.lastPushpins.filter(p => (p.options as any)?.catId !== 'bus-pushpin');
            this.updatePushpins(filtered);
            return;
        }
        this.map?.entities?.getPrimitives?.()
            .filter((p: any) => (p as { catId?: string }).catId === 'bus-pushpin')
            .forEach((pushpin: any) => {
                this.map?.entities?.remove?.(pushpin);
            });
    }
}

export default MapQuerier;
